import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

/** Ch 23.1 — Terrain editor (survey points + breaklines + contour generation). */
export default function TerrainEditorPanel({ projectId }) {
  const [terrains, setTerrains] = useState([]);
  const [active, setActive] = useState(null);
  const [contours, setContours] = useState([]);
  const [csv, setCsv] = useState("0,0,100\n100,0,105\n0,100,98\n100,100,103\n50,50,102");
  const [contourLevels, setContourLevels] = useState("99,100,101,102,103,104,105");
  const [err, setErr] = useState("");

  const reload = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/terrain`);
      setTerrains(data.terrains || []);
      if (!active && data.terrains?.[0]) {
        const r = await api.get(`/projects/${projectId}/terrain/${data.terrains[0].id}`);
        setActive(r.data.terrain);
      }
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    const name = window.prompt("Name:", `Surface ${terrains.length + 1}`);
    if (!name) return;
    const pts = csv.trim().split("\n").map((line) => {
      const [x, y, z] = line.split(",").map(Number);
      return { x, y, z };
    }).filter((p) => Number.isFinite(p.x));
    if (!pts.length) return alert("Enter at least one x,y,z line");
    try {
      const { data } = await api.post(`/projects/${projectId}/terrain`,
        { name, points: pts, cell_m: 10 });
      setActive(data.terrain); reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const importCsv = async () => {
    if (!active) return;
    const pts = csv.trim().split("\n").map((line) => {
      const [x, y, z] = line.split(",").map(Number);
      return { x, y, z };
    }).filter((p) => Number.isFinite(p.x));
    if (!pts.length) return;
    try {
      await api.post(`/projects/${projectId}/terrain/${active.id}/import-points`,
        { points: pts });
      const { data } = await api.get(
        `/projects/${projectId}/terrain/${active.id}`);
      setActive(data.terrain);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const generateContours = async () => {
    if (!active) return;
    try {
      const levels = contourLevels.split(",").map(Number).filter(Number.isFinite);
      const { data } = await api.post(
        `/projects/${projectId}/terrain/${active.id}/contours`,
        { levels });
      setContours(data.contours || []);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const op = async (operation, payload) => {
    if (!active) return;
    try {
      await api.post(`/projects/${projectId}/terrain/${active.id}/op`,
        { op: operation, payload });
      const { data } = await api.get(
        `/projects/${projectId}/terrain/${active.id}`);
      setActive(data.terrain); setContours([]);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const removeT = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/projects/${projectId}/terrain/${id}`);
    if (active?.id === id) setActive(null);
    reload();
  };

  // ── viewBox + colour ramp for points ──────────────────────────
  const { viewBox, zMin, zMax } = useMemo(() => {
    const pts = active?.points || [];
    if (!pts.length) return { viewBox: "0 0 100 100", zMin: 0, zMax: 100 };
    const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y), zs = pts.map((p) => p.z);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const w = maxX - minX || 100, h = maxY - minY || 100;
    const pad = Math.max(w, h) * 0.1 + 10;
    return { viewBox: `${minX - pad} ${-(maxY + pad)} ${w + 2 * pad} ${h + 2 * pad}`,
              zMin: Math.min(...zs), zMax: Math.max(...zs) };
  }, [active?.points]);

  const zToColor = (z) => {
    const t = (z - zMin) / Math.max(zMax - zMin, 1e-6);
    const r = Math.round(122 + 100 * t), g = Math.round(79 - 40 * t), b = Math.round(27 + 20 * t);
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div data-testid="terrain-editor" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Surfaces ({terrains.length})</div>
        {terrains.map((t) => (
          <div key={t.id} data-testid={`terrain-row-${t.id}`}
            style={{ display: "flex", gap: 4, alignItems: "center", marginTop: 3 }}>
            <button style={{ ...btnGhost, flex: 1, textAlign: "left",
                              background: active?.id === t.id ? "rgba(242,107,31,.20)" : "transparent" }}
              onClick={async () => {
                const { data } = await api.get(`/projects/${projectId}/terrain/${t.id}`);
                setActive(data.terrain); setContours([]);
              }}>
              {t.name} · {t.n_points} pts
            </button>
            <button style={{ ...btnGhost, padding: "1px 5px", fontSize: 10 }}
              onClick={() => removeT(t.id)}>×</button>
          </div>
        ))}

        <div style={{ ...label, marginTop: 12 }}>Survey CSV (x,y,z per line)</div>
        <textarea data-testid="terrain-csv" style={{ ...inputStyle, height: 100 }}
          value={csv} onChange={(e) => setCsv(e.target.value)} />
        <button data-testid="terrain-create" style={{ ...btn, width: "100%", marginTop: 6 }}
          onClick={create}>+ New surface from CSV</button>
        {active && (
          <button data-testid="terrain-import"
            style={{ ...btnGhost, width: "100%", marginTop: 4 }}
            onClick={importCsv}>↳ Append points to current surface</button>
        )}

        <div style={{ ...label, marginTop: 12 }}>Contour levels (CSV in m)</div>
        <input data-testid="terrain-levels" style={inputStyle}
          value={contourLevels} onChange={(e) => setContourLevels(e.target.value)} />
        <button data-testid="terrain-contours" style={{ ...btn, width: "100%", marginTop: 6 }}
          onClick={generateContours} disabled={!active}>Generate contours</button>

        {active && (
          <>
            <div style={{ ...label, marginTop: 12 }}>Edit ops on bbox 0,0–50,50</div>
            <button data-testid="terrain-op-grade"
              style={{ ...btnGhost, width: "100%", marginTop: 3 }}
              onClick={() => op("grade", { bbox: [0, 0, 50, 50], elevation_m: 100 })}>
              Grade pad → 100m
            </button>
            <button data-testid="terrain-op-pond"
              style={{ ...btnGhost, width: "100%", marginTop: 3 }}
              onClick={() => op("pond", { bbox: [0, 0, 50, 50], depth_m: 3 })}>
              Excavate pond −3m
            </button>
            <button data-testid="terrain-op-smooth"
              style={{ ...btnGhost, width: "100%", marginTop: 3 }}
              onClick={() => op("smooth", { bbox: [0, 0, 50, 50] })}>
              Smooth area
            </button>
          </>
        )}
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: 6, borderBottom: "1px solid var(--pf-border)",
                       fontSize: 11, color: "var(--pf-muted)" }}>
          {active ? `${active.name} · ${active.n_points} pts · cell ${active.cell_m}m · z range ${zMin.toFixed(1)}–${zMax.toFixed(1)} m`
                       : "Select or create a terrain surface"}
        </div>
        <svg viewBox={viewBox} data-testid="terrain-canvas"
              style={{ width: "100%", height: "calc(100% - 30px)", background: "#0a1220" }}>
          {/* Contours */}
          {contours.flatMap((c, ci) =>
            c.segments.map(([p1, p2], si) => (
              <line key={`c-${ci}-${si}`} x1={p1[0]} y1={-p1[1]} x2={p2[0]} y2={-p2[1]}
                      stroke="#3aa0ff" strokeWidth={0.5} opacity={0.7} />
            ))
          )}
          {/* Survey points */}
          {(active?.points || []).map((p, i) => (
            <circle key={i} cx={p.x} cy={-p.y} r={1.5}
                       fill={zToColor(p.z)} stroke="#fff" strokeWidth={0.2}
                       data-testid={`terrain-pt-${i}`}>
              <title>{`(${p.x}, ${p.y}, ${p.z.toFixed(2)})`}</title>
            </circle>
          ))}
        </svg>
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Contours generated</div>
        {!contours.length && <div style={{ color: "#888", fontSize: 11 }}>None yet</div>}
        {contours.map((c, i) => (
          <div key={i} style={{ fontSize: 11, marginTop: 4 }}>
            <span style={{ display: "inline-block", width: 12, height: 2,
                            background: "#3aa0ff", marginRight: 6, verticalAlign: "middle" }} />
            <b>{c.level_m} m</b> · {c.n_segments} segments
          </div>
        ))}
        <div style={{ ...label, marginTop: 16 }}>Help</div>
        <p style={{ fontSize: 10, color: "#aaa", lineHeight: 1.6 }}>
          Survey points are interpolated by inverse-distance (IDW) onto a regular grid.
          Contours are extracted by marching-squares. Edit ops (grade · pond · smooth)
          modify points within a bbox — re-run contours after edits.
        </p>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}
